import { ComponentTypes, Event, Tab, TabProps, TabStyle } from "@mcleod/components";
import { DOMUtil, getThemeColor, HorizontalAlignment, StringUtil } from "@mcleod/core";
import { AppTabset } from "./AppTabset";
import { ComponentPropDefinition } from "@mcleod/components/src/base/ComponentProps";
import { StickyComponent } from "../sticky/StickyComponent";
import { AppStickyManager } from "../sticky/AppStickyManager";

const defaultMarginTop = 12;

export class AppTab extends Tab {
    constructor(props?: Partial<TabProps>) {
        super({ marginTop: defaultMarginTop, ...props });
    }

    override select(event?: Event) {
        super.select(event);
        if (this._designer == null) {
            const target = (event?.target?.getEventTarget()) ?? this.heading?._element;
            if (target instanceof HTMLElement) {
                const container = this.heading?.parent?.parent?._element;
                if (container && container.id?.indexOf('tabContainer') !== -1) {
                    const targetRect = target.getBoundingClientRect();
                    const parentRect = container.getBoundingClientRect();

                    const offsetX = targetRect.left - parentRect.left +
                        container.scrollLeft - (container.clientWidth / 2) + (target.clientWidth / 2);

                    container.scrollTo({ left: offsetX, behavior: "smooth" });
                }
            }
        }
    }

    createSelector() {
        super.createSelector();
        if (this.heading) {
            this.heading.wrap = false;
        }
        if (this.headingLabel) {
            this.headingLabel._element.style.overflow = "visible";
        }
        if (this._designer == null) {
            this.heading?.setProps({ align: HorizontalAlignment.LEFT, width: "auto" });
            this.headingLabel?.setProps({ width: "auto", wrap: false, paddingLeft: 8, paddingRight: 8 });
        }
    }

    override createTitle() {
        super.createTitle();
        if ((this.tabset as AppTabset)?.originalTabStyle === TabStyle.SCROLL) {
            // App never allows scroll style and as of this writing a call to .createTitle() will never
            // create the title components that we will want to show in the app's page header context menu.
            // So call the following line to ensure it is created for the tab. And can be scooped up to be used
            // for app header actions.
            this.createTitleComponentsPanel();
        }
    }

    get defaultHeadingWidth(): number {
        return null; // Prevents default value that is used for desktop tabs
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "marginTop") {
            return defaultMarginTop;
        }
        return super.determinePropertyDefaultValue(prop);
    }

    // stickyComponent getter property defined for Component class by AppComponent#initialize.
    // Used in AppStickyManager.
    get stickyComponents(): StickyComponent[] {
        if (this.tabStyle === TabStyle.ACCORDION) {
            this.setAppPropsForTransitionEffects();

            return [{
                element: this.heading._element,
                contentComponent: this,
                onRegistration: (manager: AppStickyManager) => {
                    this.buttonExpand.addClickListener(() => {
                        manager.unstickByElement(this.heading._element);
                    });
                },
                doOnStick: () => {
                    // Add transitions for smooth effects
                    this.heading.style.transition = "background-color 1s ease-in-out, box-shadow 0.3s ease-in-out";
                    this.headingLabel.style.transition = "color 1s ease-in-out";

                    // Initial box glow effect
                    this.heading.style.boxShadow = "0 0 12px rgba(0, 123, 255, 0.8)";

                    // Set initial background color (optional, if you want a flash effect)
                    this.heading.backgroundColor = getThemeColor('primary.lightest');

                    // Change text color
                    this.headingLabel.color = getThemeColor('primary.darkest');
                    this.buttonExpand.color = getThemeColor('primary.darkest');

                    // Transition to final state after the initial glow
                    setTimeout(() => {
                        // Fade out the glow
                        this.heading.style.boxShadow = "0 2px 5px rgba(0, 0, 0, 0.1)";

                        // Transition to final background color
                        this.heading.style.backgroundColor = getThemeColor('primary.lightest');
                    }, 200);
                },
                doOnUnstick: () => {
                    // Restore original styles
                    this.heading.backgroundColor = this.heading.appProps?.originalBackgroundColor;
                    this.heading.style.boxShadow = !StringUtil.isEmptyString(this.heading.appProps?.originalBoxShadow)
                        ? this.heading.appProps?.originalBoxShadow : "none";
                    this.headingLabel.color = this.headingLabel.appProps?.originalColor;
                    this.buttonExpand.color = this.buttonExpand.appProps?.originalColor;

                    // Clear transitions
                    this.heading.style.transition = "";
                    this.headingLabel.style.transition = "";
                }
            }];
        }
        return null;
    }

    private setAppPropsForTransitionEffects() {
        this.heading.appProps ??= {};
        this.heading.appProps = {
            ...this.heading.appProps,
            originalBackgroundColor: DOMUtil.getComputedStyle('background-color', this.heading._element),
            originalBoxShadow: DOMUtil.getComputedStyle('box-shadow', this.heading._element)
        };

        this.headingLabel.appProps ??= {};
        this.headingLabel.appProps = {
            ...this.headingLabel.appProps,
            originalColor: DOMUtil.getComputedStyle('color', this.headingLabel._element)
        };

        this.buttonExpand.appProps ??= {}
        this.buttonExpand.appProps = {
            ...this.buttonExpand.appProps,
            originalColor: DOMUtil.getComputedStyle('color', this.buttonExpand._element)
        }
    }
}

ComponentTypes.registerAppComponentType("tab", {
    constructor: AppTab.prototype.constructor
});
